import Axios, { AxiosResponse } from 'axios';
import Repository from './Repository';
import ListRequest from '@/models/ListRequest';
import DeletePackages from '@/models/Subscriptions/SubscriptionPackages/DeletePackages';
import { PayForRenewingSubscription } from '@/models/Subscriptions/Payment/PayForRenewingSubscription';
import { PayForSubscription } from '@/models/Subscriptions/Payment/PayForSubscription';
import {SubscribeDto} from "@/models/Subscriptions/Payment/SubscribeDto";
import CheckSubscriptionMigration from "@/models/Subscriptions/SubscriptionPackages/CheckSubscriptionMigration";
import { NutrientSource } from '@/models/NutrientSource/NutrientSource';
import { ListResult } from './ResultTypes/ListResult';
import { ExitSurveyDto } from '@/models/Subscriptions/ExitSurveyDto';

const resource = '/subscriptions';
const exitSurveyResource = '/exitSurveys';

export default {
  fetchPackages(listRequest: ListRequest) {
    return Repository.post(`${resource}/listsubscriptionpackages`, listRequest, { withCredentials: true });
  },
  createSubscriptionPackage(createPackage: any) {
    return Repository.post(`${resource}/createSubscriptionPackage`, createPackage, { withCredentials: true });
  },
  updateSubscriptionPackage(updatePackage: any) {
    return Repository.post(`${resource}/UpdateSubscriptionPackage`, updatePackage, { withCredentials: true });
  },
  deletePackages(deletePackages: DeletePackages) {
    return Repository.post(`${resource}/deletesubscriptionpackages`, deletePackages, { withCredentials: true });
  },
  displaySubscriptionPackages(isDisplayLegacy : boolean = false) {
    return Repository.get(`${resource}/displaysubscriptionpackages?isDisplayLegacy=${isDisplayLegacy}`, { withCredentials: true });
  },
  getFastSpringAccountMenagementUrl() {
    return Repository.get(`${resource}/GetFastSpringAccountManagementUrl`, {withCredentials: true});
  },
  generateClientToken() {
    return Repository.get(`${resource}/generateclienttoken`, { withCredentials: true });
  },
  payForSubscription(payForSubscription: PayForSubscription) {
    return Repository.post(`${resource}/PayForSubscription`, payForSubscription, { withCredentials: true });
  },
  subscribe(subscribeDto: SubscribeDto) {
    return Repository.post(`${resource}/Subscribe`, subscribeDto, { withCredentials: true });
  },
  payForRenewingSubscription(payForRenewingSubscription: PayForRenewingSubscription) {
    return Repository.post(`${resource}/PayForRenewingSubscription`, payForRenewingSubscription, { withCredentials: true });
  },
  cancelSubscription() {
    return Repository.post(`${resource}/CancelSubscription`, {}, { withCredentials: true });
  },
  createExitSurvey(exitSurvey: ExitSurveyDto){
    return Repository.post(`${exitSurveyResource}/CreateExitSurvey`, exitSurvey, { withCredentials: true });
  },
  cancelFutureSubscription(subscriptionId: number){
    return Repository.post(`${resource}/CancelFutureSubscription`, {SubscriptionId: subscriptionId}, { withCredentials: true });
  },
  upgradeSubscription(subscribeDto: SubscribeDto) {
    return Repository.post(`${resource}/UpgradeSubscription`, subscribeDto, { withCredentials: true });
  },
  downgradeSubscription(subscribeDto: SubscribeDto) {
    return Repository.post(`${resource}/DowngradeSubscription`, subscribeDto, { withCredentials: true });
  },
  listCurrentSubscriptionNutrientSources() {
    return Repository.get<void, AxiosResponse<ListResult<NutrientSource>>>(
      `${resource}/ListCurrentSubscriptionNutrientSources`,
      { withCredentials: true }
    );
  },
  displaySubscriptionAddOns() {
    return Repository.get(`${resource}/DisplaySubscriptionAddOns`, { withCredentials: true });
  },
  extendTrialSubscription(data: any) {
    return Repository.post(`${resource}/ExtendTrialSubscription`, data, { withCredentials: true });
  },
  listSubscriptionPackages(listRequest: ListRequest) {
    return Repository.post(`${resource}/ListSubscriptionPackages`, listRequest, { withCredentials: true });
  },
  highlightSubscriptionPackage(id: number, isHighlighted: boolean) {
    return Repository.post(`${resource}/HighlightSubscriptionPackage`, { subscriptionPackageId: id, isHighlighted: isHighlighted }, { withCredentials: true });
  },
  flagSubscriptionPackageAsLegacy(id: number, isLegacy: boolean) {
    return Repository.post(
      `${resource}/FlagSubscriptionPackageAsLegacy`,
      { subscriptionPackageId: id, IsLegacy: isLegacy },
      { withCredentials: true }
    );
  },
  updateSubscriptionPackagesOrder(packages: number[]) {
    return Repository.post(`${resource}/UpdateSubscriptionPackageOrder`, { packages: packages }, { withCredentials: true });
  },
  getSubscriptionSystemSettings() {
    return Repository.get(`${resource}/GetSubscriptionSystemSettings`, { withCredentials: true });
  },
  setSubscriptionSystemSettings(settings: Object) {
    return Repository.post(`${resource}/SetSubscriptionSystemSettings`, settings, { withCredentials: true });
  },
  checkSubscriptionMigration(checkSubscriptionMigrationDto: CheckSubscriptionMigration) {
    return Repository.post(`${resource}/CheckSubscriptionMigration`, checkSubscriptionMigrationDto, { withCredentials: true });
  },
  recipeDishLimitExceededDetails() {
    return Repository.get(`${resource}/RecipeDishLimitExceededDetails`, { withCredentials: true });
  },
  listFutureSubscriptions() {
    return Repository.get(`${resource}/ListFutureSubscriptions`, { withCredentials: true });
  },
  removeAddOnFromCurrentSubscription(removeAddOnDto: Object) {
    return Repository.post(`${resource}/RemoveAddOnFromCurrentSubscription`, removeAddOnDto, { withCredentials: true });
  },
  listAddOnsToBuy() {
    return Repository.get(`${resource}/ListAddOnsToBuy`, { withCredentials: true });
  },
  buyAddOns(buyAddOnsDto: Object) {
    return Repository.post(`${resource}/BuyAddOns`, buyAddOnsDto, { withCredentials: true });
  },
  listPaymentMethods() {
    return Repository.get(`${resource}/ListPaymentMethods`, { withCredentials: true });
  },
  markPaymentMethodAsDefault(paymentMethodId: number) {
    return Repository.post(`${resource}/MarkPaymentMethodAsDefault`, { paymentMethodId }, { withCredentials: true });
  },
  checkUserLicensesPrice(paidUserLicenses: number) {
    return Repository.post(`${resource}/CheckUserLicensesPrice`, {PaidUserLicenses: paidUserLicenses}, { withCredentials: true });
  },
  buyUserLicenses(buyUserLicensesDto: Object) {
    return Repository.post(`${resource}/BuyUserLicenses`, buyUserLicensesDto, { withCredentials: true });
  },
  removeUserLicenses(removeUserLicensesDto: Object) {
    return Repository.post(`${resource}/RemoveUserLicenses`, removeUserLicensesDto, { withCredentials: true });
  },
  createPaymentMethod(createPaymentMethod: Object) {
    return Repository.post(`${resource}/CreatePaymentMethod`, createPaymentMethod, { withCredentials: true });
  },
  deletePaymentMethod(paymentMethodId: any) {
    return Repository.post(`${resource}/DeletePaymentMethod`, paymentMethodId, { withCredentials: true });
  },
  updatePaymentMethod(updatePaymentMethod: Object) {
    return Repository.post(`${resource}/UpdatePaymentMethod`, updatePaymentMethod, { withCredentials: true });
  },
  generateSecurePayload(payload: string) {
    let baseURL = process.env.VUE_APP_BASE_DOMAIN + '/php';
    let repository = Axios.create({
      baseURL
    });
    let encodedPayload = encodeURIComponent(payload);
    return repository.get(`${resource}/GenerateSecurePayload.php?payload=` + encodedPayload);
  }
};
